import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface FeatureToggleProviderProps<
  TFeatureToggleSet extends string | number | symbol
> {
  getFeatureToggles: () => Promise<Record<TFeatureToggleSet, boolean>>;
  children: ReactNode;
}

interface FeatureToggleContextProps<
  TFeatureToggleSet extends string | number | symbol
> {
  featureToggles: Record<TFeatureToggleSet, boolean> | null;
}

const FeatureToggleContext = createContext<
  FeatureToggleContextProps<string | number | symbol>
>({
  featureToggles: null,
});

export const FeatureToggleProvider = <
  TFeatureToggleSet extends string | number | symbol
>(
  props: FeatureToggleProviderProps<TFeatureToggleSet>
) => {
  const [featureToggles, setFeatureToggles] = useState<Record<
    TFeatureToggleSet,
    boolean
  > | null>(null);

  useEffect(() => {
    const init = async () => {
      if (!featureToggles) {
        const toggles = await props.getFeatureToggles();
        setFeatureToggles({ ...toggles });
      }
    };

    init();
  }, [featureToggles]);

  return (
    <FeatureToggleContext.Provider
      value={{
        featureToggles,
      }}
    >
      {props.children}
    </FeatureToggleContext.Provider>
  );
};

export const useFeatureToggle = <
  TFeatureToggleSet extends string | number | symbol
>(): FeatureToggleContextProps<TFeatureToggleSet> => {
  const context = useContext(
    FeatureToggleContext
  ) as FeatureToggleContextProps<TFeatureToggleSet>;
  if (!context) {
    throw new Error(
      "useFeatureToggle must be used within a FeatureToggleProvider"
    );
  }
  return context;
};
